import { useEffect, useMemo, useState } from "react";
import { Accessor } from "../types";
import {
  CountryCode,
  CountryName,
  countryCodeSet,
  countryCodeToCountry,
  countryNameToCodeAndFlag,
} from "./countries";

type UsePhoneInputProps = Accessor<string | null, "phoneNumber">;

export default function usePhoneInput({
  phoneNumber,
  setPhoneNumber,
}: UsePhoneInputProps) {
  const [countryCode, setCountryCode] = useState(
    (phoneNumber && phoneNumber.split("-")[0]) || "1"
  );
  const [localNumber, setLocalNumber] = useState(
    (phoneNumber && phoneNumber.split("-").slice(1).join("")) || ""
  );

  const countryName = useMemo(
    () => countryCodeToCountry[countryCode] as CountryName,
    [countryCode]
  );
  const countryFlag = useMemo(
    () => countryNameToCodeAndFlag[countryName]?.flag ?? "🏴",
    [countryName]
  );

  useEffect(() => {
    setPhoneNumber(
      localNumber
        ? `${countryCode && extractNumbers(countryCode)}${
            countryCode && localNumber && "-"
          }${extractNumbers(localNumber)}`
        : null
    );
  }, [countryCode, localNumber, setPhoneNumber]);

  const error = useMemo(() => {
    if (countryCode && !countryCodeSet.has(countryCode as CountryCode))
      return "Please select a valid country code";
    if (
      phoneNumber &&
      !(
        phoneNumber.match(/^[1-9]\d{0,3}-\d{4,14}$/) &&
        extractNumbers(phoneNumber).match(/^[1-9]\d{6,14}$/)
      )
    )
      return "Please check if the phone number is valid";
  }, [countryCode, localNumber, phoneNumber]);

  return {
    countryCode,
    setCountryCode,
    localNumber,
    setLocalNumber,
    error,
    countryName,
    countryFlag,
  };
}

function extractNumbers(input: string): string {
  return input.replace(/[^0-9]/g, "");
}
